// src/date.ts
function toDate(value) {
  if (!value) return void 0;
  return value instanceof Date ? value : new Date(value);
}
function getStartOfToday() {
  const d = /* @__PURE__ */ new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}
function getEndOfToday() {
  const d = /* @__PURE__ */ new Date();
  d.setHours(23, 59, 59, 999);
  return d.toISOString();
}

// src/jwt.ts
function decodeJwtPayload(token) {
  try {
    const segment = token.split(".")[1];
    if (!segment) return null;
    const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, "=");
    const json = atob(padded);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

// src/scope.ts
import { ROLE_SCOPE } from "@hetivo/hetivo-lib-types";

// src/utils/array.ts
function deduplicate(items) {
  return Array.from(new Set(items));
}
function chunked(items, size) {
  if (!Number.isInteger(size) || size < 1) {
    throw new RangeError(`chunked size must be a positive integer, received ${String(size)}`);
  }
  const chunks = [];
  for (let start = 0; start < items.length; start += size) {
    chunks.push(items.slice(start, start + size));
  }
  return chunks;
}

// src/utils/concurrency.ts
function createConcurrencyLimiter(limit) {
  if (!Number.isInteger(limit) || limit < 1) {
    throw new RangeError(`concurrency limit must be a positive integer, received ${String(limit)}`);
  }
  let active = 0;
  const waiters = [];
  const acquire = async () => {
    if (active < limit) {
      active += 1;
      return;
    }
    await new Promise((resolve) => {
      waiters.push(resolve);
    });
  };
  const release = () => {
    const resume = waiters.shift();
    if (resume) {
      resume();
    } else {
      active -= 1;
    }
  };
  return async (task) => {
    await acquire();
    try {
      return await task();
    } finally {
      release();
    }
  };
}

// src/utils/list.ts
function truncateList(items, maxVisible) {
  const visible = items.slice(0, maxVisible);
  const overflow = items.length - maxVisible;
  const base = visible.join(", ");
  if (overflow <= 0) {
    return base;
  }
  return base ? `${base} +${String(overflow)}` : `+${String(overflow)}`;
}

// src/utils/object.ts
function pickExcept(source, keys) {
  const result = {};
  for (const [key, value] of Object.entries(source)) {
    if (!keys.includes(key)) {
      result[key] = value;
    }
  }
  return result;
}
function keepDefined(source) {
  const result = {};
  for (const [key, value] of Object.entries(source)) {
    if (value !== void 0 && value !== null) {
      result[key] = value;
    }
  }
  return result;
}

// src/utils/pagination.ts
function toPaginatedResponse(data) {
  if (data.pageSize <= 0) {
    throw new Error(`[toPaginatedResponse] pageSize must be > 0, received '${data.pageSize}'`);
  }
  return {
    items: data.items,
    page: data.page,
    pageSize: data.pageSize,
    total: data.total,
    totalPages: Math.ceil(data.total / data.pageSize)
  };
}

// src/utils/retry.ts
async function retryWithDelay(fn, options) {
  const { delayMs = 1e3, maxRetries = 3 } = options ?? {};
  let lastError;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (attempt < maxRetries) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
    }
  }
  throw lastError;
}

// src/scope.ts
function getScopeIdsByLevel(scopes, level) {
  return scopes.filter((s) => s.level === level).map((s) => s.id);
}
function getScopesByLevel(scopes, level) {
  return scopes.filter((s) => s.level === level);
}
function buildScopesFromIds(territoryIds, locationIds) {
  const territoryScopes = territoryIds.map((id) => ({ id, level: ROLE_SCOPE.territory }));
  const locationScopes = locationIds.map((id) => ({ id, level: ROLE_SCOPE.location }));
  return [...territoryScopes, ...locationScopes];
}
function getParentTerritoryIdsFromResolvedScopes(resolvedScopes) {
  const parentIds = resolvedScopes.filter(
    (s) => s.level === ROLE_SCOPE.location && !!s.parentTerritoryId
  ).map((s) => s.parentTerritoryId);
  return deduplicate(parentIds);
}

// src/string.ts
function toKebabCase(value) {
  return value.toLowerCase().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
}
function computeInitials(name, fallback = "??") {
  if (!name?.trim()) {
    return fallback;
  }
  return name.trim().split(/\s+/).map((part) => part[0]).join("").toUpperCase().slice(0, 2);
}
function getFullName(firstName, lastName) {
  return [firstName, lastName].map((part) => part?.trim()).filter(Boolean).join(" ");
}
function getDisplayName(user) {
  if (!user) return void 0;
  const full = getFullName(user.firstName, user.lastName);
  return full || void 0;
}
function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
function buildEscapedRegExp(value, caseInsensitive = true) {
  return new RegExp(escapeRegExp(value), caseInsensitive ? "i" : "");
}
var SEPARATORS = /[\s-]/g;
function buildSeparatorTolerantRegExp(value) {
  const characters = Array.from(value.trim().replace(SEPARATORS, ""));
  if (characters.length === 0) return null;
  return new RegExp(characters.map(escapeRegExp).join("[\\s-]*"), "i");
}
function buildAnchoredRegExp(value, caseInsensitive = true) {
  return new RegExp(`^${escapeRegExp(value)}$`, caseInsensitive ? "i" : "");
}
function humanize(value) {
  return value.split(/[-_]/).map((word) => {
    const lower = word.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  }).join(" ");
}
function placeholderLabel(token) {
  const bare = token.replace(/^[{(\s]+/, "").replace(/[})\s]+$/, "");
  return humanize(bare.replace(/\./g, "_"));
}

// src/index.ts
function createErrorResponse(error) {
  return {
    error,
    success: false
  };
}
function createSuccessResponse(data) {
  return {
    data,
    success: true
  };
}
function formatDateIntl(date, locale = "en") {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "long",
    timeStyle: "short"
  }).format(date);
}
function generateId() {
  const crypto = typeof globalThis !== "undefined" && "crypto" in globalThis ? globalThis.crypto : void 0;
  if (crypto?.randomUUID) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === "x" ? r : r & 3 | 8;
    return v.toString(16);
  });
}
function getApiData(response) {
  return response.success ? response.data : void 0;
}
function isApiError(response) {
  return !response.success;
}
export {
  buildAnchoredRegExp,
  buildEscapedRegExp,
  buildScopesFromIds,
  buildSeparatorTolerantRegExp,
  chunked,
  computeInitials,
  createConcurrencyLimiter,
  createErrorResponse,
  createSuccessResponse,
  decodeJwtPayload,
  deduplicate,
  escapeRegExp,
  formatDateIntl,
  generateId,
  getApiData,
  getDisplayName,
  getEndOfToday,
  getFullName,
  getParentTerritoryIdsFromResolvedScopes,
  getScopeIdsByLevel,
  getScopesByLevel,
  getStartOfToday,
  humanize,
  isApiError,
  keepDefined,
  pickExcept,
  placeholderLabel,
  retryWithDelay,
  toDate,
  toKebabCase,
  toPaginatedResponse,
  truncateList
};
//# sourceMappingURL=index.mjs.map